import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useMemo, useState } from 'react';

export type EncryptedTranscriptionMetadataInspectButtonProps = {
  /** Redacted API snapshot (transcript strings stripped server-side before storage). */
  transcriptionMetadata: unknown;
};

const formatStoredValueForAdmin = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/**
 * Shows stored transcription API metadata with transcript text already removed on the server.
 * Admin does not decrypt anything; this JSON is not the clinical transcript.
 */
export const EncryptedTranscriptionMetadataInspectButton = ({
  transcriptionMetadata,
}: EncryptedTranscriptionMetadataInspectButtonProps) => {
  const [open, setOpen] = useState(false);

  const body = useMemo(
    () => formatStoredValueForAdmin(transcriptionMetadata),
    [transcriptionMetadata],
  );

  const { hasSegments, hasWords } = useMemo(() => {
    if (
      transcriptionMetadata === null ||
      transcriptionMetadata === undefined ||
      typeof transcriptionMetadata !== 'object'
    ) {
      return { hasSegments: false, hasWords: false };
    }
    const o = transcriptionMetadata as Record<string, unknown>;
    const segs = Array.isArray(o.segments) ? o.segments : [];
    const words = Array.isArray(o.words) ? o.words : [];
    return {
      hasSegments: segs.length > 0,
      hasWords: words.length > 0,
    };
  }, [transcriptionMetadata]);

  return (
    <>
      <Tooltip title="View redacted transcription API metadata (no transcript text stored here)">
        <IconButton size="small" onClick={() => setOpen(true)}>
          <DescriptionOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Transcription metadata (redacted)</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Transcript wording is stripped before this document is saved. The JSON
            below is whatever the speech API returned, minus text fields—often{' '}
            <code>usage</code> only for models that respond with{' '}
            <code>text</code> + <code>usage</code>. The admin app does not decrypt
            user data.
          </Alert>
          {!hasSegments && !hasWords && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              There are no <code>segments</code> or <code>words</code> in this
              snapshot. The transcription API often omits those for{' '}
              <code>response_format: &apos;json&apos;</code> (typical for GPT-4o
              transcribe–style deployments). Segment and word timings are requested
              only for Whisper-style or diarize transcription deployments in the
              backend, which return richer objects before redaction.
            </Alert>
          )}
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontSize: 12,
              overflow: 'auto',
              maxHeight: 'min(70vh, 520px)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {body}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
